// recStartedSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AnyAction, Dispatch } from "redux";
import { appendAlert } from "../redux/alertSlice";
import apiAxios from "../utils/apiAxios";
import { formatDateTime } from "../utils/dateUtils";
import { getUserName } from "../utils/utils";
import { GridApi, ColumnApi } from "ag-grid-enterprise";
import { PostProcList, Action } from "../models/PostProcList";

// 作業状態
export type RecStatus = "stopped" | "started" | "suspend" | "disable";

export const recStartedSlice = createSlice({
  name: "recStarted",
  initialState: {
    status: "stopped" as RecStatus,
  },
  reducers: {
    setRecStatus: (state, action: PayloadAction<RecStatus>) => {
      state.status = action.payload;
    },
  },
});

// ボタンの種類と加工実績の状態でPostするActionを決定する
const createPostAction = (recStatus: RecStatus, procStatus: number): Action => {
  if (recStatus === "stopped") {
    return "end";
  }
  if (recStatus === "started") {
    if (procStatus === 2) {
      return "resume";
    } else {
      return "start";
    }
  }
  if (recStatus === "suspend") {
    return "suspend";
  }
  return "end";
};

const { setRecStatus } = recStartedSlice.actions;

// 作業状況更新APIにPostするデータ作成と作業状況一覧の更新
const createPostData = (
  gridApi: GridApi | null,
  columnApi: ColumnApi | null,
  status: RecStatus,
  timestamp: string,
  userName: string,
  suspensionQtys: string[]
) => {
  // ステータス 0-未作業, 1-作業中, 2-一時中断, 3-再開, 4-作業完了
  const postData: PostProcList[] = [];
  const removeDatas: PostProcList[] = [];
  let suspensionQtysIndex = 0;

  gridApi?.forEachNode((rowNode: any, index: number) => {
    // 開始ボタンでかつ開始済みで未選択の場合は終了状態へ更新
    if (
      status === "started" &&
      !rowNode.isSelected() &&
      (rowNode.data.proc_status === 1 || rowNode.data.proc_status === 3)
    ) {
      postData.push({
        po_data_num: rowNode.data.po_data_num,
        po_desc_num: rowNode.data.po_desc_num,
        mach_seq: rowNode.data.mach_seq,
        mach_code: rowNode.data.mach_code,
        action: "end",
        timestamp: timestamp,
        update_by: userName,
        qty_before_suspension: 0,
        suspension_code: 0,
      });
      removeDatas.push(rowNode.data);
    }

    if (rowNode.isSelected()) {
      // 中断の場合は中間実績数をセット
      let qty_before_suspension = 0;
      if (status === "suspend") {
        qty_before_suspension = Number(suspensionQtys[suspensionQtysIndex]);
        suspensionQtysIndex++;
      }

      postData.push({
        po_data_num: rowNode.data.po_data_num,
        po_desc_num: rowNode.data.po_desc_num,
        mach_seq: Number(rowNode.data.mach_seq),
        mach_code: rowNode.data.mach_code,
        action: createPostAction(status, rowNode.data.proc_status),
        timestamp: timestamp,
        update_by: userName,
        qty_before_suspension: qty_before_suspension,
        suspension_code: 0,
      });

      // 画面の加工実績一覧の更新
      if (status === "stopped") {
        removeDatas.push(rowNode.data);
      } else {
        // 一覧のステータスを更新
        let updateData = { ...rowNode.data };
        updateData.proc_status = updateProcState(
          status,
          rowNode.data.proc_status
        );
        // 開始したら緊急を更新＆選択解除
        if (status === "started") {
          updateData.urgency_level = 0;
          updateData.sort_order = 2;
          rowNode.setSelected(false);
        }
        // 中断の場合、現計画数-中断時の実績数
        if (status === "suspend") {
          updateData.current_qty -= qty_before_suspension;
        }
        rowNode.setData(updateData);
      }
    }
  });
  // 完了状態は一覧から取り除く
  const res = gridApi?.applyTransaction({ remove: removeDatas });
  console.log(res);

  columnApi?.applyColumnState({
    state: [],
    defaultState: { sort: null },
  });

  columnApi?.applyColumnState({
    state: [{ colId: "sort_order", sort: "desc", sortIndex: 0 }],
    defaultState: { sort: null },
  });

  return postData;
};

const updateProcState = (status: RecStatus, proc_status: number) => {
  switch (status) {
    case "started":
      // 中断からの開始は再開
      if (proc_status === 2) {
        return 3;
      }
      return 1;
    case "suspend":
      return 2;
    case "stopped":
      return 4;
  }
  return 0;
};

// 加工の作業開始、終了、中断を行い、"加工実績"テーブルを更新する(API実行)
const postProcList = (
  gridApi: GridApi | null,
  columnApi: ColumnApi | null,
  status: RecStatus,
  suspensionQtys: string[]
) => {
  return async (dispatch: Dispatch<AnyAction>) => {
    try {
      const timestamp = formatDateTime(new Date());
      const userName = (await getUserName().then()).toString();
      const postData = createPostData(
        gridApi,
        columnApi,
        status,
        timestamp,
        userName,
        suspensionQtys
      );
      console.log(postData);

      // 加工実績更新API
      const response = await apiAxios.post<PostProcList>(
        `/proc_list`,
        postData
      );
      console.log(response.data);
      // ステータスの更新
      if (status === "suspend") {
        dispatch(setRecStatus("suspend"));
      } else {
        dispatch(setRecStatus("disable"));
      }
      
    } catch (error: any) {
      console.error(error);
      dispatch(
        appendAlert({
          severity: "error",
          message: "加工実績テーブルの更新に失敗しました。" + error.message,
          displayTime: 5000,
        })
      );
    }
  };
};

export { setRecStatus, postProcList };
export default recStartedSlice.reducer;
